import type { PriceValidation, SizeValidation } from "../exchange-betting-potential/ExchangeBettingPotential.types";
import { getBetslipExchangeEdit } from "../betslip-module/betslip-module-selectors";
import { getExchangeOrder } from "../exchange-orders/exchange-orders-selectors";
import { type BetslipExchangePersistenceType } from "../exchange-orders/ExchangeOrders.types";
import { getUnmatchedBets } from "./exchange-betting-selectors";
import type { URN, ExchangeSide } from "../../../types";
import type { BetslipApplicationState } from "../BetslipApplicationState";
import type { OrderEdit } from "../betslip-module/betslip-module.types";

function isEditStateValid(order: OrderEdit | undefined): boolean {
  if (order === undefined) {
    return true;
  }

  if (order.validations.price || order.validations.size) {
    return false;
  }

  return order.price !== null && order.size !== null;
}

const getDisplayValue = (current: undefined | number | null, initial: number): number | undefined => {
  if (current === undefined) {
    return initial;
  }
  const currentOrEmpty = current ?? undefined;
  return currentOrEmpty;
};

export type EditingBetState = {
  price?: number;
  originalPrice?: number;
  size?: number;
  originalSize?: number;
  side: ExchangeSide;
  profit?: number;
  liability?: number;
  payout?: number;
  persistenceType: BetslipExchangePersistenceType;
  isValid: boolean;
  priceValidation?: PriceValidation;
  sizeValidation?: SizeValidation;
  hasPriceChanged: boolean;
  hasSizeChanged: boolean;
  hasPersistenceTypeChanged: boolean;
};

export const getEditingBetState = (state: BetslipApplicationState, market: URN): EditingBetState | null => {
  const edit = getBetslipExchangeEdit(state);
  if (!edit) {
    return null;
  }

  const { betId, order: orderEdit } = edit;

  const [unmatchedBet] = getUnmatchedBets(state, market, [betId]);
  const order = getExchangeOrder(state, market, betId);

  if (!unmatchedBet || !order) {
    return null;
  }

  const isValid = isEditStateValid(orderEdit);

  const originalPrice = order.price;
  const originalSize = unmatchedBet.size;
  const originalPersistence = order.persistenceType;

  const price = getDisplayValue(orderEdit?.price, originalPrice);
  const size = getDisplayValue(orderEdit?.size, originalSize);
  const persistenceType = orderEdit?.persistenceType || originalPersistence;

  const profit = isValid ? unmatchedBet.profit : undefined;
  const liability = isValid ? unmatchedBet.liability : undefined;
  const payout = isValid ? unmatchedBet.payout : undefined;

  return {
    price,
    originalPrice,
    size,
    originalSize,
    side: order.side,
    persistenceType,
    profit,
    liability,
    payout,
    isValid,
    priceValidation: orderEdit?.validations.price?.data || undefined,
    sizeValidation: orderEdit?.validations.size?.data || undefined,
    hasPriceChanged: price !== originalPrice,
    hasSizeChanged: size !== originalSize,
    hasPersistenceTypeChanged: persistenceType !== originalPersistence,
  };
};
